import React, { useEffect, useMemo, useState } from "react";
import {
  FaPause,
  FaPlay,
  FaStop,
  FaVolumeUp,
} from "react-icons/fa";
import { extractSpeechChunks } from "../../utils/speechTextExtractor";
import { useSpeechReader } from "../../hooks/useSpeechReader";

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5];

const getStatusLabel = (status, unsupported) => {
  if (unsupported || status === "unsupported") return "Speech not supported";
  if (status === "playing") return "Playing";
  if (status === "paused") return "Paused";
  if (status === "stopped") return "Stopped";
  return "Ready";
};

const getPrimaryControl = (status) => {
  if (status === "playing") {
    return {
      label: "Pause",
      ariaLabel: "Pause article",
      Icon: FaPause,
      action: "pause",
    };
  }

  if (status === "paused") {
    return {
      label: "Resume",
      ariaLabel: "Resume article",
      Icon: FaPlay,
      action: "resume",
    };
  }

  return {
    label: "Play",
    ariaLabel: "Play article",
    Icon: FaPlay,
    action: "play",
  };
};

const BlogVoiceReader = ({ contentRootRef, postId, disabled = false }) => {
  const [chunks, setChunks] = useState([]);

  useEffect(() => {
    if (disabled || !contentRootRef?.current) {
      setChunks([]);
      return;
    }

    setChunks(extractSpeechChunks(contentRootRef.current));
  }, [contentRootRef, disabled, postId]);

  const {
    status,
    unsupported,
    selectedStartChunkId,
    rate,
    setRate,
    voices,
    selectedVoiceURI,
    setSelectedVoiceURI,
    isAutoScrollEnabled,
    setIsAutoScrollEnabled,
    play,
    pause,
    resume,
    stop,
    selectChunk,
  } = useSpeechReader({ chunks, postId });

  const isDisabled = disabled || unsupported || chunks.length === 0;
  const statusLabel = useMemo(
    () => getStatusLabel(status, unsupported),
    [status, unsupported],
  );
  const primaryControl = useMemo(() => getPrimaryControl(status), [status]);
  const PrimaryIcon = primaryControl.Icon;

  useEffect(() => {
    const rootElement = contentRootRef?.current;
    if (disabled || !rootElement) return undefined;

    const handleChunkClick = (event) => {
      if (
        event.target.closest(
          "a, button, input, select, textarea, pre, code, .code-block",
        )
      ) {
        return;
      }

      const chunkElement = event.target.closest("[data-speech-chunk-id]");
      if (!chunkElement || !rootElement.contains(chunkElement)) return;

      selectChunk(chunkElement.dataset.speechChunkId);
    };

    rootElement.addEventListener("click", handleChunkClick);

    return () => {
      rootElement.removeEventListener("click", handleChunkClick);
    };
  }, [contentRootRef, disabled, postId, selectChunk]);

  const handlePrimaryClick = () => {
    if (primaryControl.action === "pause") {
      pause();
      return;
    }

    if (primaryControl.action === "resume") {
      resume();
      return;
    }

    play();
  };

  return (
    <section
      className={`blog-voice-reader ${isDisabled ? "is-disabled" : ""}`}
      aria-label="Blog voice reader"
    >
      <div className="blog-voice-reader__title">
        <FaVolumeUp aria-hidden="true" />
        <span>Listen to article</span>
      </div>

      <div className="blog-voice-reader__controls">
        <button
          type="button"
          className="blog-voice-reader__button blog-voice-reader__button--primary"
          onClick={handlePrimaryClick}
          disabled={isDisabled}
          aria-label={primaryControl.ariaLabel}
        >
          <PrimaryIcon aria-hidden="true" />
          <span>{primaryControl.label}</span>
        </button>
        <button
          type="button"
          className="blog-voice-reader__button"
          onClick={() => stop()}
          disabled={
            isDisabled ||
            ((status === "idle" || status === "stopped") &&
              !selectedStartChunkId)
          }
          aria-label="Stop article"
        >
          <FaStop aria-hidden="true" />
          <span>Stop</span>
        </button>

        <label className="blog-voice-reader__field">
          <span>Reading speed</span>
          <select
            className="blog-voice-reader__select"
            value={rate}
            onChange={(event) => setRate(event.target.value)}
            disabled={unsupported}
            aria-label="Reading speed"
          >
            {SPEED_OPTIONS.map((speed) => (
              <option value={speed} key={speed}>
                {speed}x
              </option>
            ))}
          </select>
        </label>

        <label className="blog-voice-reader__field blog-voice-reader__voice-field">
          <span>Voice</span>
          <select
            className="blog-voice-reader__select blog-voice-reader__voice-select"
            value={selectedVoiceURI}
            onChange={(event) => setSelectedVoiceURI(event.target.value)}
            disabled={unsupported || voices.length === 0}
            aria-label="Voice"
          >
            <option value="">System default</option>
            {voices.map((voice) => (
              <option value={voice.voiceURI} key={voice.voiceURI}>
                {voice.name} — {voice.lang || "unknown"}
              </option>
            ))}
          </select>
        </label>

        <label className="blog-voice-reader__toggle">
          <input
            type="checkbox"
            checked={isAutoScrollEnabled}
            onChange={(event) => setIsAutoScrollEnabled(event.target.checked)}
            disabled={isDisabled}
          />
          <span>Auto-scroll</span>
        </label>

        <span className="blog-voice-reader__status" aria-live="polite">
          {statusLabel}
        </span>
      </div>

      <p className="blog-voice-reader__hint">
        Tip: click any paragraph to start reading from there.
      </p>
    </section>
  );
};

export default BlogVoiceReader;
